import React from 'react';
import {
  BookMarked,
  GitMerge,
  Settings,
  Share2,
  Calculator,
  ArrowRightLeft,
  Layers,
  Menu,
  History,
  LayoutGrid,
  LayoutTemplate,
  Database,
  Combine,
  Box,
  Briefcase,
  Variable,
  DollarSign,
  AppWindow,
  Eraser,
  Copy,
  Table,
  ClipboardCheck,
} from 'lucide-react';
import type { AppSidebarNavItem } from 'ui-common-components/shell';
import type { MenuConfigItem } from '@/types';
import type { User } from '@/types';

export interface NavItem {
  path: string;
  label: string;
  icon: React.ElementType;
  end?: boolean;
  divider?: boolean;
  dividerLabel?: string;
  disabled?: boolean;
  /** When set, only users whose role is in this list can see the item. */
  roles?: string[];
  /** Nested sub-tabs rendered as an expandable group in the sidebar. */
  children?: NavItem[];
}

// ─── Role lists (reused in nav items + route guards) ─────────────────────────

/** Roles that can access admin-only screens. */
export const ADMIN_ONLY: string[] = ['admin'];

/** Roles that can access configuration / rule screens. */
export const ADMIN_AND_CM: string[] = ['admin', 'consolidation_manager'];

/** All roles except viewer (i.e. roles that can create / mutate data). */
export const ALL_EXCEPT_VIEWER: string[] = [
  'admin',
  'consolidation_manager',
  'preparer',
  'reviewer',
  'accountant',
  'auditor',
];

// ─── Feature flags (sprint) ──────────────────────────────────────────────────
// Dev=true / prod=false (see docker-compose*.yml). The nav item only renders
// when the flag is explicitly 'true' so the production sidebar stays unchanged
// until the feature is ready to ship.
const SMART_VIEW_NAV_ENABLED =
  String(import.meta.env.VITE_SMART_VIEW_NAV_ENABLED ?? 'false').toLowerCase() === 'true';

export const ALL_NAV_ITEMS: NavItem[] = [
  { path: '/dimensions', label: 'Dimensions', icon: Layers },
  // Names Tables (Alias Tables) is now reached via Dimension Manager → Other Segment,
  // so it is no longer a standalone sidebar item. The /setup/alias-tables route remains.
  { path: '/valid-intersections', label: 'Valid Intersections', icon: Combine, roles: ADMIN_AND_CM },
  { path: '/forms', label: 'Forms', icon: LayoutGrid },
  { path: '/data', label: 'Data', icon: Database },
  // Smart View — the spreadsheet-style data-entry screen. Route exists at
  // App.tsx:151; the nav entry is behind VITE_SMART_VIEW_NAV_ENABLED so it only
  // shows in dev until the sprint ships. Without the flag, /smart-view is still
  // reachable via the Data → Actions → "New Ad hoc Grid" / "Open in Smart View"
  // buttons (the pre-sprint path).
  ...(SMART_VIEW_NAV_ENABLED
    ? [{ path: '/smart-view', label: 'Smart View', icon: Table } as NavItem]
    : []),
  { path: '/application-cubes', label: 'Application Cubes', icon: Box, roles: ADMIN_AND_CM },
  { path: '/manage-ownership', label: 'Manage Ownership', icon: GitMerge },
  {
    path: '/entity-ownership-structure',
    label: 'Ownership Structure',
    icon: Share2,
  },
  { path: '/data-exchange', label: 'Data Exchange', icon: ArrowRightLeft },
  { path: '/journals', label: 'Journals', icon: BookMarked, roles: ALL_EXCEPT_VIEWER },
  // Old Consolidation page — removed from sidebar. Route still exists at
  // /old-consolidation for deep links (Dashboard cards, GlobalSearch), but
  // it's no longer visible in the navigation menu.
  // { path: '/old-consolidation', label: 'Old Consolidation', icon: GitMerge },
  // Reporting Studio — new Reporting module (/reporting/*), visible to all
  // authenticated roles (viewers see published output per Oracle parity).
  { path: '/reporting', label: 'Reporting Studio', icon: LayoutTemplate },
  { path: '/audit', label: 'Audit Trail', icon: History },
  { path: '/currency-triangulation', label: 'Currency Triangulation', icon: Calculator },
  // US-CM-8: Consolidation Waterfall review screen (Step 12 style) —
  // review tool for all non-viewer roles (AC3: reviewer+).
  // US-DS-001: Oracle FCCS Data Status — Calculation / Node / Approval status
  // by entity x period, with the prior-period close gate. Read-only for
  // preparer/reviewer/accountant/auditor; Consolidate & Translate are
  // gated to admin + consolidation_manager in the router.
  { path: '/data-status', label: 'Data Status', icon: ClipboardCheck, roles: ALL_EXCEPT_VIEWER },
  { path: '/consolidation-waterfall', label: 'Consolidation Waterfall', icon: Layers, roles: ALL_EXCEPT_VIEWER },
  // SDM supplemental rate entry + RefreshSDMCurrencyRates sync (US-R5.1)
  { path: '/sdm-rates', label: 'SDM Rates', icon: DollarSign, roles: ADMIN_AND_CM },
  // Consolidation Process now lives as a tab inside "Ownership Structure"
  // (/entity-ownership-structure) — the standalone nav entry is removed so the
  // ownership view and the consolidation process sit on one page.  The
  // /consolidation-rules route still exists (deep links + the embedded tab).
  // { path: '/consolidation-rules', label: 'Consolidation Process', icon: Scale, roles: ADMIN_AND_CM },
  // Calc Manager nav entry hidden — calculation rules now live in the Rules ›
  // Process tab (the Configurable insertion-point modal, backend-driven).  The
  // standalone /calc-manager route still exists but is no longer surfaced.
  { path: '/variables', label: 'Variables', icon: Variable, roles: ADMIN_AND_CM },
  // Application → maintenance utilities that act on the whole application
  // (Oracle FCCS parity: Application ▸ Clear Data / Copy Data).
  {
    path: '/application',
    label: 'Application',
    icon: AppWindow,
    roles: ADMIN_AND_CM,
    children: [
      { path: '/application/clear-data', label: 'Clear Data', icon: Eraser, roles: ADMIN_AND_CM },
      { path: '/application/copy-data', label: 'Copy Data', icon: Copy, roles: ADMIN_AND_CM },
    ],
  },
  { path: '/jobs', label: 'Jobs', icon: Briefcase, roles: ADMIN_AND_CM },
  { path: '/menu-control', label: 'Menu Control', icon: Menu, roles: ADMIN_ONLY },
  { path: '/settings', label: 'Settings', icon: Settings, roles: ADMIN_ONLY },
];

/** Parents + all nested children in one flat list (title/active-id lookups). */
export function flattenNavItems(items: NavItem[]): NavItem[] {
  return items.flatMap((item) =>
    item.children && item.children.length > 0 ? [item, ...flattenNavItems(item.children)] : [item],
  );
}

export function mergeNavItemsWithConfig(
  allItems: NavItem[],
  menuConfig: MenuConfigItem[] | null,
  menuLoading: boolean,
): NavItem[] {
  if (menuLoading) return [];
  if (!menuConfig || menuConfig.length === 0) return allItems;

  const configMap = new Map(menuConfig.map((c) => [c.menu_key, c]));
  const canonicalIndex = new Map(
    allItems.map((item, i) => [item.path, i] as const),
  );

  let ordered = allItems
    .filter((item) => {
      const cfg = configMap.get(item.path);
      return cfg ? cfg.is_visible : true;
    })
    // Children carry their own config rows (e.g. /application/clear-data), so a
    // child hidden in Menu Control must drop out of the submenu too — and a parent
    // left with no children is dead weight, same rule as filterNavForUser.
    .map((item) => {
      if (!item.children || item.children.length === 0) return item;
      const children = item.children
        .filter((child) => configMap.get(child.path)?.is_visible ?? true)
        .sort(
          (a, b) =>
            (configMap.get(a.path)?.sort_order ?? 9999) -
            (configMap.get(b.path)?.sort_order ?? 9999),
        );
      return { ...item, children };
    })
    .filter((item) => !item.children || item.children.length > 0)
    .sort((a, b) => {
      const ao = configMap.get(a.path)?.sort_order ?? 9999;
      const bo = configMap.get(b.path)?.sort_order ?? 9999;
      if (ao !== bo) return ao - bo;
      // Stable order when sort_order ties: follow ALL_NAV (e.g. Forms then Data, not /data on top)
      return (
        (canonicalIndex.get(a.path) ?? 0) - (canonicalIndex.get(b.path) ?? 0)
      );
    });

  // Per product spec: "Data" sits directly under "Forms" in the sidebar, regardless
  // of per-tenant sort_order (e.g. Menu Control drag order or legacy DB).
  const dataItem = ordered.find((i) => i.path === '/data');
  if (dataItem) {
    const hasForms = ordered.some((i) => i.path === '/forms');
    if (hasForms) {
      ordered = ordered.filter((i) => i.path !== '/data');
      const formIdx = ordered.findIndex((i) => i.path === '/forms');
      ordered = [
        ...ordered.slice(0, formIdx + 1),
        dataItem,
        ...ordered.slice(formIdx + 1),
      ];
    }
  }

  return ordered;
}

export function filterNavForUser(items: NavItem[], user: User | null): NavItem[] {
  if (!user) return [];
  const role = user.role;
  return items
    .filter((item) => {
      if (item.roles && item.roles.length > 0) {
        return item.roles.includes(role);
      }
      return true;
    })
    .map((item) =>
      item.children && item.children.length > 0
        ? { ...item, children: filterNavForUser(item.children, user) }
        : item,
    )
    // A parent that declares children but has none left for this role is dead weight.
    .filter((item) => !item.children || item.children.length > 0);
}

export function toAppSidebarNavItems(items: NavItem[]): AppSidebarNavItem[] {
  return items.map((item) => ({
    id: item.path.replace(/^\//, ''),
    label: item.label,
    icon: React.createElement(item.icon, { className: 'w-4 h-4 shrink-0' }),
    disabled: item.disabled,
    ...(item.children && item.children.length > 0
      ? { children: toAppSidebarNavItems(item.children), defaultExpanded: false }
      : {}),
  }));
}

/** Sidebar / topbar label for the current route (prefers nav catalog, then static map). */
export function resolvePageTitle(pathname: string): string {
  const stripped = pathname.replace(/^\//, '').replace(/\/$/, '');
  if (!stripped) return 'Dashboard';

  // Longest path first so /application/clear-data wins over /application.
  const navMatch = flattenNavItems(ALL_NAV_ITEMS)
    .slice()
    .sort((a, b) => b.path.length - a.path.length)
    .find((item) => {
      const id = item.path.replace(/^\//, '');
      return stripped === id || stripped.startsWith(`${id}/`);
    });
  if (navMatch) return navMatch.label;

  const first = stripped.split('/')[0] ?? '';
  return first
    .split('-')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function pathToActiveNavId(pathname: string): string | null {
  const stripped = pathname.replace(/^\//, '').replace(/\/$/, '');
  if (!stripped) return null;

  const navIds = flattenNavItems(ALL_NAV_ITEMS).map((item) => item.path.replace(/^\//, ''));

  // Exact path (e.g. application-cubes — must not fall through to entity-* routes)
  if (navIds.includes(stripped)) return stripped;

  // Longest prefix for nested routes (integration/connections)
  const prefixMatches = navIds
    .filter((id) => stripped === id || stripped.startsWith(`${id}/`))
    .sort((a, b) => b.length - a.length);
  if (prefixMatches.length > 0) return prefixMatches[0];

  const first = stripped.split('/')[0];
  return first && navIds.includes(first) ? first : first || null;
}
